import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Wrench, Repeat, Check, ArrowRight, Loader2, Sparkles, Mail, Copy, X } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { getContactEmail } from "@/components/ProtectedEmail";
import { useAuth } from "@/hooks/useAuth";
import api from "@/lib/api";
import { toast } from "sonner";

const Tag = ({ children }) => (
  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F]">{children}</span>
);

const Bullet = ({ children }) => (
  <li className="flex gap-2.5 items-start font-mono text-sm text-[#E0D8CA]">
    <Check className="w-4 h-4 text-[#D95B38] mt-0.5 shrink-0" strokeWidth={1.8} />
    <span>{children}</span>
  </li>
);

const SERVICE_TYPES = [
  { id: "setup", label: "Done-for-you setup" },
  { id: "reseller", label: "White-label / reseller" },
  { id: "other", label: "Something else" },
];

export default function ServicesPage() {
  const { user } = useAuth({ redirectIfUnauthed: false });
  const [form, setForm] = useState({
    name: "",
    email: "",
    service_type: "setup",
    business_name: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/inquiries", form);
      setSent(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const selectType = (id) => setForm({ ...form, service_type: id });

  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] grain">
      <Navbar user={user} />

      {/* HERO */}
      <section className="mx-auto max-w-6xl px-6 md:px-12 pt-20 pb-16">
        <Tag>For agencies, freelancers & implementation partners</Tag>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl mt-4 leading-[0.95] tracking-tight">
          We'll build it for you.<br />
          <span className="italic text-[#D95B38]">Or you build it for them.</span>
        </h1>
        <p className="font-mono text-base text-[#A39A8F] mt-8 max-w-2xl leading-relaxed">
          Not every customer wants to fiddle with prompts and Twilio webhooks. Pick the service that matches how you want to work.
        </p>
      </section>

      {/* TWO OFFERS */}
      <section className="mx-auto max-w-6xl px-6 md:px-12 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Done-for-you */}
          <div data-testid="service-setup" className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-10 flex flex-col">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 border border-[#D95B38] flex items-center justify-center">
                <Wrench className="w-5 h-5 text-[#D95B38]" strokeWidth={1.5} />
              </div>
              <Tag>Service 01</Tag>
            </div>
            <h2 className="font-serif text-3xl mb-2">Done-for-you setup.</h2>
            <div className="font-mono text-sm text-[#A39A8F] mb-6">From <span className="text-[#F5F2EB] text-lg">£299</span> one-time</div>
            <p className="font-mono text-sm text-[#E0D8CA] mb-5 leading-relaxed">
              You tell us about your business. We build, train, connect, and hand you a working AI agent ready to take customer messages.
            </p>
            <ul className="space-y-2.5 mb-8 flex-1">
              <Bullet>Bespoke persona &amp; system prompt written for your tone of voice</Bullet>
              <Bullet>Up to 10 knowledge documents uploaded &amp; tested (menu, FAQs, policies)</Bullet>
              <Bullet>WhatsApp connection — sandbox or your real business number</Bullet>
              <Bullet>Intake form configured for your lead capture (name, phone, service needed)</Bullet>
              <Bullet>Brand-matched widget for your website (colours, logo, copy)</Bullet>
              <Bullet>60-min onboarding call &amp; 30 days of email support</Bullet>
            </ul>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-3">Typical timeline</div>
            <div className="font-mono text-xs text-[#E0D8CA] mb-6">3–5 working days from kick-off to live.</div>
            <a href="#inquiry" onClick={() => selectType("setup")}
              data-testid="service-setup-cta"
              className="self-start font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-6 py-3 btn-press inline-flex items-center gap-2">
              Request setup <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* White-label reseller */}
          <div data-testid="service-reseller" className="bg-[#1A1715] border border-[#D95B38]/40 p-8 md:p-10 flex flex-col relative">
            <div className="absolute top-6 right-6"><Tag>Best margins</Tag></div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 border border-[#D95B38] bg-[#D95B38]/10 flex items-center justify-center">
                <Repeat className="w-5 h-5 text-[#D95B38]" strokeWidth={1.5} />
              </div>
              <Tag>Service 02</Tag>
            </div>
            <h2 className="font-serif text-3xl mb-2">White-label reseller.</h2>
            <div className="font-mono text-sm text-[#A39A8F] mb-6">Custom partner pricing</div>
            <p className="font-mono text-sm text-[#E0D8CA] mb-5 leading-relaxed">
              Build agents for your own clients, charge what you like, keep the relationship. AgentReply stays in the background — your brand sits on top.
            </p>
            <ul className="space-y-2.5 mb-8 flex-1">
              <Bullet>Each client gets their own agent under your account</Bullet>
              <Bullet>Widget &amp; chat page fully brand-able (your colours, logo, footer text)</Bullet>
              <Bullet>You set the monthly price — we suggest £49–£99/agent/month for SMBs</Bullet>
              <Bullet>Discounted partner pricing on volume (5+ agents)</Bullet>
              <Bullet>Priority email support &amp; a private Slack channel</Bullet>
              <Bullet>Early access to live phone calls &amp; ElevenLabs voices when they launch</Bullet>
            </ul>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] mb-3">Who this is for</div>
            <div className="font-mono text-xs text-[#E0D8CA] mb-6">Marketing agencies, web designers, freelancers serving 5+ local businesses.</div>
            <a href="#inquiry" onClick={() => selectType("reseller")}
              data-testid="service-reseller-cta"
              className="self-start font-mono text-xs uppercase tracking-[0.25em] border border-[#D95B38] text-[#D95B38] px-6 py-3 hover:bg-[#D95B38] hover:text-[#F5F2EB] transition-colors inline-flex items-center gap-2">
              Become a partner <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section id="inquiry" className="mx-auto max-w-3xl px-6 md:px-12 pb-24 scroll-mt-24">
        <div className="bg-[#1A1715] border border-[#F5F2EB]/10 p-8 md:p-12">
          <Tag>Get in touch</Tag>
          <h2 className="font-serif text-3xl md:text-4xl mt-3 mb-3">Tell us what you need.</h2>
          <p className="font-mono text-sm text-[#A39A8F] mb-10 leading-relaxed">
            We reply within 24 hours, Monday to Friday. No spam, no auto-responders.
          </p>

          {sent ? (
            <div data-testid="inquiry-success" className="text-center py-12">
              <Sparkles className="w-8 h-8 text-[#D95B38] mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl mb-3">Got it.</h3>
              <p className="font-mono text-sm text-[#A39A8F] max-w-md mx-auto">
                Thanks {form.name?.split(" ")[0] || "for reaching out"}. We'll reply to <span className="text-[#F5F2EB]">{form.email}</span> within 24 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="inquiry-form">
              {/* Service type selector */}
              <div className="mb-8">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] block mb-3">I'm interested in…</span>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {SERVICE_TYPES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => selectType(t.id)}
                      data-testid={`inquiry-type-${t.id}`}
                      className={`font-mono text-xs uppercase tracking-[0.2em] px-3 py-3 border transition-colors ${
                        form.service_type === t.id
                          ? "border-[#D95B38] bg-[#D95B38]/10 text-[#D95B38]"
                          : "border-[#F5F2EB]/20 text-[#A39A8F] hover:border-[#F5F2EB]/40 hover:text-[#F5F2EB]"
                      }`}
                    >{t.label}</button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <Label text="Your name">
                  <Input required value={form.name} onChange={update("name")} data-testid="inquiry-name" autoComplete="name" />
                </Label>
                <Label text="Email">
                  <Input required type="email" value={form.email} onChange={update("email")} data-testid="inquiry-email" autoComplete="email" />
                </Label>
                <Label text="Business name (optional)">
                  <Input value={form.business_name} onChange={update("business_name")} data-testid="inquiry-business" autoComplete="organization" />
                </Label>
                <Label text="Phone (optional)">
                  <Input value={form.phone} onChange={update("phone")} data-testid="inquiry-phone" autoComplete="tel" inputMode="tel" />
                </Label>
              </div>

              <Label text="Tell us a bit about what you need">
                <textarea
                  value={form.message} onChange={update("message")}
                  data-testid="inquiry-message"
                  rows={5} maxLength={4000}
                  placeholder="e.g. I run a 3-location dental practice and want an agent on WhatsApp to book consultations. Closed Sundays, fluent in English & Spanish."
                  className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38] resize-none"
                />
              </Label>

              {error && <div className="font-mono text-xs text-[#D95B38] mt-4" data-testid="inquiry-error">{error}</div>}

              <button type="submit" disabled={loading} data-testid="inquiry-submit"
                className="mt-8 font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] px-8 py-4 btn-press disabled:opacity-60 inline-flex items-center gap-3">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Sending…" : "Send inquiry →"}
              </button>
            </form>
          )}

          <div className="mt-10 pt-6 border-t border-[#F5F2EB]/10">
            <button
              type="button"
              onClick={() => setShowEmailModal(true)}
              data-testid="open-contact-email-modal"
              className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] hover:text-[#D95B38] transition-colors inline-flex items-center gap-2"
            >
              <Mail className="w-3 h-3" strokeWidth={1.8} />
              Prefer email? Show contact address →
            </button>
          </div>
        </div>
      </section>

      {showEmailModal && <ContactEmailModal onClose={() => setShowEmailModal(false)} />}

      <Footer />
    </div>
  );
}

const Label = ({ text, children }) => (
  <label className="block mb-5">
    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] block mb-2">{text}</span>
    {children}
  </label>
);
const Input = (props) => (
  <input {...props}
    className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38]" />
);

function ContactEmailModal({ onClose }) {
  // Email is only decoded on user action — never lives in the DOM otherwise.
  const [email, setEmail] = React.useState("");
  const [revealed, setRevealed] = React.useState(false);

  const reveal = () => {
    const addr = getContactEmail();
    setEmail(addr);
    setRevealed(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success("Email copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please select and copy manually");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="contact-email-modal"
    >
      <div
        className="relative w-full max-w-md bg-[#1A1715] border border-[#F5F2EB]/15 p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="contact-email-modal-close"
          className="absolute top-4 right-4 text-[#A39A8F] hover:text-[#F5F2EB] transition-colors"
        >
          <X className="w-5 h-5" strokeWidth={1.6} />
        </button>

        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-3 flex items-center gap-2">
          <Mail className="w-3 h-3" /> Contact us
        </div>
        <h3 className="font-serif text-2xl text-[#F5F2EB] mb-3 leading-tight">
          Reach the team directly
        </h3>
        <p className="font-mono text-xs text-[#A39A8F] leading-relaxed mb-6">
          We typically reply within one business day. For partnership or done-for-you inquiries the form above is faster — we route those straight to the founders.
        </p>

        {!revealed ? (
          <button
            type="button"
            onClick={reveal}
            data-testid="contact-email-reveal"
            className="w-full font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] hover:bg-[#c14d2e] transition-colors py-3 inline-flex items-center justify-center gap-2 btn-press"
          >
            <Mail className="w-3.5 h-3.5" />
            Show email address
          </button>
        ) : (
          <div className="space-y-3">
            <div
              className="font-mono text-sm text-[#F5F2EB] border border-[#F5F2EB]/15 px-4 py-3 break-all select-all bg-black/30"
              data-testid="contact-email-value"
            >
              {email}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <a
                href={`mailto:${email}`}
                data-testid="contact-email-mailto"
                className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#D95B38] text-[#D95B38] hover:bg-[#D95B38] hover:text-[#F5F2EB] transition-colors py-3 inline-flex items-center justify-center gap-2"
              >
                <Mail className="w-3 h-3" />
                Open email app
              </a>
              <button
                type="button"
                onClick={copy}
                data-testid="contact-email-copy"
                className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#F5F2EB]/20 text-[#E0D8CA] hover:border-[#F5F2EB]/40 transition-colors py-3 inline-flex items-center justify-center gap-2"
              >
                <Copy className="w-3 h-3" />
                Copy
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
